// AuthActionsCell：账号 / 机器 auth 列表行操作按钮。
// 按 status 决定可见组合：
//   pending → 通过审批 / 拒绝(=disable)
//   verified→ 禁用 / 吊销
//   revoked → 重新启用(=verify) / 已吊销不可逆
//   disabled→ 启用 / 吊销
// destructive 类（拒绝、吊销）走 ConfirmDialog 点击确认；其余直接提交。

import { useState } from "react";
import { Button } from "@/components/ui/Button";
import { ConfirmDialog } from "./ConfirmDialog";
import { apiPostForm, APIError } from "@/api/client";
import { toast } from "@/components/ui/Sonner";
import {
  accountAuthActionPath,
  machineAuthActionPath,
  type AuthAction,
} from "@/api/endpoints";

export type AuthDomain = "account" | "machine";

export interface AuthActionsCellProps {
  domain: AuthDomain;
  authID: number;
  status: string;
  subject: string;
  actor: string;
  canWrite: boolean;
  onChanged?: () => void;
}

function actionPath(domain: AuthDomain, authID: number, action: AuthAction) {
  return domain === "account"
    ? accountAuthActionPath(authID, action)
    : machineAuthActionPath(authID, action);
}

interface ActionSpec {
  action: AuthAction;
  label: string;
  variant: "default" | "destructive" | "outline" | "ghost";
  // 需要二次确认才允许提交（destructive 类）。
  needsConfirm: boolean;
  confirmDescription?: string;
}

function actionsForStatus(status: string, subject: string): ActionSpec[] {
  switch (status) {
    case "pending":
      return [
        { action: "verify", label: "通过审批", variant: "default", needsConfirm: false },
        {
          action: "disable",
          label: "拒绝",
          variant: "outline",
          needsConfirm: true,
          confirmDescription: `拒绝并禁用 ${subject}`,
        },
      ];
    case "verified":
      return [
        {
          action: "disable",
          label: "禁用",
          variant: "outline",
          needsConfirm: true,
          confirmDescription: `禁用 ${subject}；可随时再启用`,
        },
        {
          action: "revoke",
          label: "吊销",
          variant: "destructive",
          needsConfirm: true,
          confirmDescription: `吊销 ${subject}；吊销后无法再反连，需重新走 pending 审批`,
        },
      ];
    case "disabled":
      return [
        { action: "verify", label: "启用", variant: "default", needsConfirm: false },
        {
          action: "revoke",
          label: "吊销",
          variant: "destructive",
          needsConfirm: true,
          confirmDescription: `吊销 ${subject}；吊销后无法再反连，需重新走 pending 审批`,
        },
      ];
    case "revoked":
      // 后端允许 revoked → verify 重新启用；但需要明确确认。
      return [
        {
          action: "verify",
          label: "重新启用",
          variant: "outline",
          needsConfirm: true,
          confirmDescription: `重新启用已吊销的 ${subject}`,
        },
      ];
    default:
      return [];
  }
}

export function AuthActionsCell({
  domain,
  authID,
  status,
  subject,
  actor,
  canWrite,
  onChanged,
}: AuthActionsCellProps) {
  const [pending, setPending] = useState<AuthAction | null>(null);
  const [confirm, setConfirm] = useState<ActionSpec | null>(null);
  const actions = actionsForStatus(status, subject);

  async function fire(spec: ActionSpec) {
    setPending(spec.action);
    try {
      await apiPostForm(actionPath(domain, authID, spec.action), {});
      toast.success(`${spec.label} 成功`, { description: subject });
      onChanged?.();
    } catch (err) {
      const msg =
        err instanceof APIError ? err.message : err instanceof Error ? err.message : String(err);
      toast.error(`${spec.label} 失败`, { description: msg });
    } finally {
      setPending(null);
    }
  }

  if (actions.length === 0) {
    return <span className="text-[hsl(var(--muted-foreground))]">—</span>;
  }

  return (
    <div className="flex flex-wrap gap-1">
      {actions.map((spec) => (
        <Button
          key={spec.action}
          type="button"
          size="sm"
          variant={spec.variant}
          disabled={!canWrite || pending !== null}
          onClick={() => {
            if (spec.needsConfirm) setConfirm(spec);
            else void fire(spec);
          }}
        >
          {pending === spec.action ? "…" : spec.label}
        </Button>
      ))}
      {confirm ? (
        <ConfirmDialog
          open
          onOpenChange={(open) => {
            if (!open) setConfirm(null);
          }}
          resource={domain === "account" ? "account_auth" : "machine_auth"}
          action={confirm.action}
          target={`${authID}`}
          actor={actor}
          description={confirm.confirmDescription}
          onConfirm={async () => {
            const spec = confirm;
            setConfirm(null);
            await fire(spec);
          }}
        />
      ) : null}
    </div>
  );
}
